import {
  AdvancedMarker,
  Map as GoogleMap,
  InfoWindow,
  Pin,
  useMap,
  useMapsLibrary,
} from "@vis.gl/react-google-maps"
import { MarkerClusterer, type Marker as MarkerType } from "@googlemaps/markerclusterer"
import { FC, ReactElement, useCallback, useEffect, useMemo, useState } from "react"
import { Site } from "../../generated/proto-ts/main"
import { formatCoordinatesMultiline } from "../../utils/maps"
import { StatEnvelope } from "../../utils/statEnvelope"
import { FlexCol } from "./common"
import { SyntheticImpactWithEmbeddedReferences } from "../../usercomm/common/usercommAsyncRequestGeneric"
import { COLOR_BG_BLUE, COLOR_BG_LIGHT_BLUE, pbUUIDToUuid } from "../../utils/utils"
import dayjs from "dayjs"
import { Link } from "react-router-dom"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faExternalLink } from "@fortawesome/free-solid-svg-icons"
import { AimOutlined } from "@ant-design/icons"
import { message, Space, AutoComplete, Tooltip, Button } from "antd"
import { ICoordinate } from "../../types"
// import { IPApiResult, getIPLocation } from "../../utils/ipapi"

interface Coordinate {
  lat: number
  lng: number
}
interface TsCoordinate extends Coordinate {
  timestamp: number
}
export interface LabeledTsCoordinate extends TsCoordinate {
  key: string
  header: ReactElement | null
  description: ReactElement | null
}

const ClusteredMarker: FC<{
  _key: string
  position: { lat: number; lng: number }
  setMarkerRef: (marker: MarkerType | null, key: string) => void
  onMarkerClick?: () => void
}> = ({ _key, position, setMarkerRef, onMarkerClick }) => {
  //   const [marker, setLocalMarker] = useState<google.maps.marker.AdvancedMarkerElement | null>(null)

  const ref = useCallback(
    (marker: google.maps.marker.AdvancedMarkerElement) => {
      setMarkerRef(marker, _key)
      //   setLocalMarker(marker)
    },
    [setMarkerRef, _key],
  )

  const onClick = useCallback(() => {
    if (!onMarkerClick) {
      return
    }
    onMarkerClick()
  }, [onMarkerClick])

  return <AdvancedMarker key={_key} position={position} ref={ref} onClick={onClick} />
}

export const ClusteredMap: FC<{
  points: LabeledTsCoordinate[] | null
  unclusteredPoints: LabeledTsCoordinate[] | null
  externalCenter?: Coordinate | null
}> = ({ points, unclusteredPoints, externalCenter }) => {
  const [center, setCenter] = useState<Coordinate | null>(null)
  const [markers, setMarkers] = useState<{ [key: string]: MarkerType }>({})
  // create the markerClusterer once the map is available and update it when
  // the markers are changed
  const map = useMap()
  const clusterer = useMemo(() => {
    if (!map) return null

    return new MarkerClusterer({ map })
  }, [map])

  useEffect(() => {
    if (externalCenter === null || externalCenter === undefined) {
      return
    }
    setCenter(externalCenter)
    if (map !== null) {
      map.setCenter(externalCenter)
    }
  }, [map, externalCenter])

  useEffect(() => {
    let _points = points
    if (_points === null) {
      _points = unclusteredPoints
    }
    if (_points === null || _points.length === 0) {
      return
    }
    let lngEnvelope = new StatEnvelope()
    let latEnvelope = new StatEnvelope()
    for (let coord of _points) {
      lngEnvelope.addValue(coord.lng)
      latEnvelope.addValue(coord.lat)
    }
    if (latEnvelope.n === 0 || lngEnvelope.n === 0) {
      setCenter(null)
      return
    }
    let lat = latEnvelope.mean
    let lng = lngEnvelope.mean
    setCenter({
      lat,
      lng,
    })
    if (map) {
      let bounds = new google.maps.LatLngBounds(
        { lat: latEnvelope.min, lng: lngEnvelope.min },
        { lat: latEnvelope.max, lng: lngEnvelope.max },
      )
      //   console.log("ClusterMap: fitBounds: ", bounds)
      map.fitBounds(bounds, 120)
    }
  }, [map, points, unclusteredPoints])

  useEffect(() => {
    if (!clusterer) {
      return
    }
    clusterer.clearMarkers()
    clusterer.addMarkers(Object.values(markers))
  }, [clusterer, markers])

  // this callback will effectively get passsed as ref to the markers to keep
  // tracks of markers currently on the map
  const setMarkerRef = useCallback((marker: MarkerType | null, key: string) => {
    setMarkers((markers) => {
      if ((marker && markers[key]) || (!marker && !markers[key])) return markers
      if (marker) {
        return { ...markers, [key]: marker }
      } else {
        const { [key]: _, ...newMarkers } = markers
        return newMarkers
      }
    })
  }, [])

  // InfoWindow
  const [selectedCoord, setSelectedCoord] = useState<LabeledTsCoordinate | null>(null)
  const [infoWindowShown, setInfoWindowShown] = useState(false)

  const onMarkerClick = useCallback(
    (coord: LabeledTsCoordinate | null) => {
      setSelectedCoord(coord)
      if (coord?.key === selectedCoord?.key) {
        setInfoWindowShown((prev) => !prev)
      } else {
        setInfoWindowShown(true)
      }
    },
    [selectedCoord?.key],
  )

  const onMapClick = useCallback(() => {
    setSelectedCoord(null)
    setInfoWindowShown(false)
  }, [])

  if (center === null) {
    return null
  }

  return (
    <div
      style={{
        width: "100%",
        height: 300,
      }}
    >
      <GoogleMap
        defaultCenter={center}
        defaultZoom={15}
        mapId={"DEMO_MAP_ID"}
        streetViewControl={false}
        mapTypeControlOptions={{
          mapTypeIds: ["roadmap", "satellite"],
          position: 1,
        }}
        onClick={onMapClick}
      >
        {/* Unclustered markers */}
        {unclusteredPoints &&
          unclusteredPoints.map((point) => (
            <AdvancedMarker
              key={point.key}
              position={point}
              onClick={() => {
                onMarkerClick(point)
              }}
            >
              <Pin
                glyphColor={COLOR_BG_BLUE}
                borderColor={COLOR_BG_BLUE}
                background={COLOR_BG_LIGHT_BLUE}
              />
            </AdvancedMarker>
          ))}
        {/* Clustered markers */}
        {points &&
          points.map((point) => (
            <ClusteredMarker
              _key={point.key}
              position={point}
              setMarkerRef={setMarkerRef}
              onMarkerClick={() => {
                onMarkerClick(point)
              }}
            />
          ))}
        {infoWindowShown && selectedCoord && (
          <InfoWindow
            position={selectedCoord}
            pixelOffset={[0, -40]}
            onCloseClick={() => {
              setInfoWindowShown(false)
            }}
            headerContent={selectedCoord.header}
          >
            {selectedCoord.description}
          </InfoWindow>
        )}
      </GoogleMap>
    </div>
  )
}

export const SitesClusterMap: FC<{
  impacts: SyntheticImpactWithEmbeddedReferences[] | null
  sites: Site[] | null
}> = ({ impacts, sites }) => {
  const [points, setPoints] = useState<LabeledTsCoordinate[] | null>(null)

  useEffect(() => {
    if (sites === null || impacts === null) {
      return
    }

    let _siteImpactsMap: Record<string, SyntheticImpactWithEmbeddedReferences[]> = {}
    for (let impact of impacts) {
      if (impact.impact.deleted_at > 0) {
        continue
      }
      if (impact.site !== null) {
        let siteUUID = pbUUIDToUuid(impact.site.uuid)
        if (_siteImpactsMap[siteUUID] === undefined) {
          _siteImpactsMap[siteUUID] = []
        }
        _siteImpactsMap[siteUUID]!.push(impact)
      }
    }

    let points: LabeledTsCoordinate[] = []
    for (let [siteUUID, siteImpacts] of Object.entries(_siteImpactsMap)) {
      let site = sites.find((s) => pbUUIDToUuid(s.uuid) === siteUUID)
      if (!site) {
        continue
      }
      let latStatEnv = new StatEnvelope()
      let lonStatEnv = new StatEnvelope()

      let header = (
        <Link to={`/sites/${siteUUID}`}>
          <b>{site.site_name}</b> <FontAwesomeIcon icon={faExternalLink} />
        </Link>
      )
      let description = (
        <FlexCol style={{ gap: 0 }}>
          <div>
            <b>Mission:</b> {site.mission_name}
          </div>
          <div>
            <b>Client: </b> {site.client_name}
          </div>
          <div>
            <b>Date: </b> {dayjs(site.created_at).format("YYYY-MM-DD")}
          </div>
        </FlexCol>
      )

      for (let impact of siteImpacts) {
        if (impact.impact.latitude === 0 && impact.impact.longitude === 0) {
          continue
        }
        latStatEnv.addValue(impact.impact.latitude)
        lonStatEnv.addValue(impact.impact.longitude)
      }
      if (latStatEnv.n === 0 || lonStatEnv.n === 0) {
        continue
      }
      let meanLat = latStatEnv.mean
      let meanLon = lonStatEnv.mean
      points.push({
        key: siteUUID,
        timestamp: site.created_at,
        lat: meanLat,
        lng: meanLon,
        header,
        description,
      })
    }
    setPoints(points)
  }, [sites, impacts])

  return <ClusteredMap points={points} unclusteredPoints={null} />
}

export const GoogleAddressWidget: FC<{
  address: string | null
  setAddress: (address: string | null) => void
  points: LabeledTsCoordinate[] | null
  autocompleteClassName?: string
  externalSetSelectedCoordinates?: (coords: ICoordinate | null) => void
}> = ({ address, setAddress, points, autocompleteClassName, externalSetSelectedCoordinates }) => {
  const [antdMessageCtx, antdMessageCtxHolder] = message.useMessage()

  const placesLib = useMapsLibrary("places")

  const [autocompleteService, setAutocompleteService] =
    useState<google.maps.places.AutocompleteService | null>(null)
  const [geocoderService, setGeocoderService] = useState<google.maps.Geocoder | null>(null)
  const [sessionToken, setSessionToken] =
    useState<google.maps.places.AutocompleteSessionToken | null>(null)
  const [placePredictions, setPlacePredictions] = useState<
    google.maps.places.AutocompletePrediction[]
  >([])

  const [selectedPoint, _setSelectedPoint] = useState<ICoordinate | null>(null)
  const [biasGeoposition, setBiasGeoposition] = useState<ICoordinate | null>(null)

  //   const [ipApiResult, setIpApiResult] = useState<IPApiResult | null>(null)

  const [value, setValue] = useState<string>("")
  //   const { stationSensors } = useUsercommContextBLE()

  const setSelectedPoint = useCallback(
    (coords: ICoordinate | null) => {
      _setSelectedPoint(coords)
      if (externalSetSelectedCoordinates) {
        externalSetSelectedCoordinates(coords)
      }
      //   console.debug(`GoogleAddressWidget: setSelectedPoint`, coords)
    },
    [externalSetSelectedCoordinates, _setSelectedPoint],
  )

  useEffect(() => {
    if (placesLib === null) {
      return
    }
    let autocompleteService = new google.maps.places.AutocompleteService()
    setAutocompleteService(autocompleteService)

    let geocoderService = new google.maps.Geocoder()
    setGeocoderService(geocoderService)

    let sessionToken = new google.maps.places.AutocompleteSessionToken()
    setSessionToken(sessionToken)
  }, [placesLib])

  //   useEffect(() => {
  //     getIPLocation().then((ipApiResult) => {
  //       setIpApiResult(ipApiResult)
  //     })
  //   }, [])

  const memoExternalCenter: Coordinate | null = useMemo(() => {
    if (selectedPoint === null) {
      return null
    }
    let center = {
      lat: selectedPoint.coords.latitude,
      lng: selectedPoint.coords.longitude,
    }
    // console.debug(`GoogleAddressWidget: memoExternalCenter`, center)
    return center
  }, [selectedPoint])

  const memoMarkerBarycenter = useMemo(() => {
    if (points === null || points.length === 0) {
      return null
    }
    let sumLat = 0
    let sumLng = 0
    let cnt = 0
    for (let coord of points) {
      sumLat += coord.lat
      sumLng += coord.lng
      cnt++
    }
    let meanLat = sumLat / cnt
    let meanLng = sumLng / cnt
    return {
      coords: {
        latitude: meanLat,
        longitude: meanLng,
      },
    }
  }, [points])

  const memoUnclusteredPoints = useMemo((): LabeledTsCoordinate[] | null => {
    if (selectedPoint === null) {
      return null
    }
    return [
      {
        key: "selected_address_coord",
        timestamp: 0,
        lat: selectedPoint.coords.latitude ?? 0,
        lng: selectedPoint.coords.longitude ?? 0,
        header: <b>{value}</b>,
        description: (
          <pre>
            {formatCoordinatesMultiline({
              lat: selectedPoint.coords.latitude ?? 0,
              lng: selectedPoint.coords.longitude ?? 0,
            })}
          </pre>
        ),
      },
    ]
  }, [selectedPoint, value])

  useEffect(() => {
    if (memoMarkerBarycenter !== null) {
      console.debug(
        `GoogleAddressWidget: bias geoposition thru memoMarkerBarycenter`,
        memoMarkerBarycenter,
      )
      setBiasGeoposition(memoMarkerBarycenter)
      return
    }
    // if (ipApiResult !== null) {
    //   console.debug(`GoogleAddressWidget: bias geoposition thru ipapi`, ipApiResult)
    //   setBiasGeoposition({
    //     coords: {
    //       latitude: ipApiResult.latitude,
    //       longitude: ipApiResult.longitude,
    //     },
    //   })
    //   return
    // }
  }, [memoMarkerBarycenter])

  const getPlacePredictionsOnChangeMutCB = useCallback(
    (value: string) => {
      if (autocompleteService === null || sessionToken === null) {
        return
      }
      autocompleteService.getPlacePredictions(
        {
          sessionToken: sessionToken,
          input: value,
          locationBias: biasGeoposition
            ? new google.maps.Circle({
                center: {
                  lat: biasGeoposition.coords.latitude,
                  lng: biasGeoposition.coords.longitude,
                },
                radius: 1000,
              })
            : undefined,
        },
        (predictions, status) => {
          if (status !== google.maps.places.PlacesServiceStatus.OK || predictions === null) {
            antdMessageCtx.warning(`GooglePlaces PlcaePredictions NOK: ${status}`)
            return
          }
          setPlacePredictions(predictions)
        },
      )
    },
    [autocompleteService, sessionToken, biasGeoposition],
  )

  const biasGeopositionOnClickMutCB = useCallback(() => {
    if (biasGeoposition === null) {
      return
    }
    if (geocoderService !== null) {
      geocoderService.geocode(
        {
          location: {
            lat: biasGeoposition.coords.latitude,
            lng: biasGeoposition.coords.longitude,
          },
        },
        (results, status) => {
          console.log(`GooglePlaces: nearbySearch`, results)
          if (status !== google.maps.GeocoderStatus.OK || results === null) {
            antdMessageCtx.warning(`GooglePlaces Geocoder NOK: ${status}`)
            return
          }
          let firstResult = results[0]
          if (firstResult === undefined) {
            return
          }
          setValue(firstResult.formatted_address ?? "")
          setAddress(firstResult.formatted_address ?? "")
          if (firstResult.geometry !== undefined && firstResult.geometry.location !== undefined) {
            let latLng = firstResult.geometry.location
            setSelectedPoint({
              coords: {
                latitude: latLng.lat(),
                longitude: latLng.lng(),
              },
            })
          }
        },
      )
    }
  }, [biasGeoposition, geocoderService])

  useEffect(() => {
    if (address === "" || geocoderService === null) {
      return
    }
    geocoderService.geocode(
      {
        address: address,
      },
      (results, status) => {
        console.log(`GooglePlaces: geocode`, results)
        if (status !== google.maps.GeocoderStatus.OK || results === null) {
          antdMessageCtx.warning(`GooglePlaces Geocoder NOK: ${status}`)
          return
        }
        let firstResult = results[0]
        if (firstResult === undefined) {
          return
        }
        setValue(firstResult.formatted_address ?? "")
        // console.debug(`GooglePlaces: geocode result`, firstResult)
        if (firstResult.geometry !== undefined && firstResult.geometry.location !== undefined) {
          let latLng = firstResult.geometry.location
          setSelectedPoint({
            coords: {
              latitude: latLng.lat(),
              longitude: latLng.lng(),
            },
          })
        }
      },
    )
  }, [address, geocoderService])

  useEffect(() => {
    if (points !== null && points.length > 0) {
      let sumLat = 0
      let sumLng = 0
      let cnt = 0
      for (let coord of points) {
        sumLat += coord.lat
        sumLng += coord.lng
        cnt++
      }
      let meanLat = sumLat / cnt
      let meanLng = sumLng / cnt
      setSelectedPoint({
        coords: {
          latitude: meanLat,
          longitude: meanLng,
        },
      })
      return
    }
  }, [points])

  return (
    <>
      {antdMessageCtxHolder}
      <FlexCol>
        <Space.Compact style={{ width: "100%" }} className={autocompleteClassName}>
          <AutoComplete
            allowClear
            style={{ width: "100%" }}
            value={value}
            onChange={(value) => {
              if (value === "") {
                setAddress("")
                setSelectedPoint(null)
              }
              getPlacePredictionsOnChangeMutCB(value)
              setValue(value)
            }}
            onSelect={(_, option) => {
              console.log(`GooglePlaces: onSelect`, option)
              setAddress(option.value)
            }}
            options={placePredictions.map((item) => ({
              key: item.place_id,
              value: item.description,
            }))}
          />
          <Tooltip title="Auto-detect address" overlayInnerStyle={{ textAlign: "center" }}>
            <Button
              type={"default"}
              onClick={() => {
                biasGeopositionOnClickMutCB()
              }}
            >
              <AimOutlined />
            </Button>
          </Tooltip>
        </Space.Compact>
        <ClusteredMap
          points={points}
          unclusteredPoints={memoUnclusteredPoints}
          externalCenter={memoExternalCenter}
        />
      </FlexCol>
    </>
  )
}
